import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronDown, ChevronLeft, ChevronRight, Images } from "lucide-react";
import api, { imgSrc } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useIsMobile, ExpandPill } from "@/components/MobileCollapse";
import { useLang } from "@/context/LanguageContext";

function PhotoStack({ photos, title }) {
  const stack = (photos || []).slice(0, 3);
  if (!stack.length) {
    return (
      <span className="flex h-14 w-[4.25rem] sm:h-16 sm:w-20 shrink-0 items-center justify-center rounded-xl border border-slate-800 bg-[#0C1220] text-slate-600">
        <Images className="h-5 w-5" />
      </span>
    );
  }
  const transforms = ["-rotate-6 -translate-x-1 translate-y-1", "rotate-0 z-10", "rotate-6 translate-x-1 -translate-y-1"];
  return (
    <span className="relative block h-14 w-[4.25rem] sm:h-16 sm:w-20 shrink-0">
      {stack.map((p, i) => (
        <img
          key={p + i}
          src={imgSrc(p, 200)}
          alt={`${title} anteprima ${i + 1}`}
          loading="lazy"
          className={`absolute inset-0 h-full w-full rounded-lg border-2 border-[#0C1220] object-cover shadow-lg ${transforms[i] || ""}`}
        />
      ))}
    </span>
  );
}

function AlbumRow({ album, open, onToggle, onPhoto }) {
  const { t } = useLang();
  return (
    <div
      className={`overflow-hidden rounded-2xl border backdrop-blur-md transition-colors duration-300 ${open ? "border-amber-400/40 bg-slate-900/60" : "border-slate-800/80 bg-slate-900/40 hover:border-slate-700"}`}
      data-testid={`album-card-${album.id}`}
    >
      <button onClick={onToggle} aria-expanded={open} className="flex w-full items-center gap-3.5 sm:gap-4 p-3 sm:p-3.5 text-left focus:outline-none">
        <PhotoStack photos={album.photos?.length ? album.photos : (album.cover_url ? [album.cover_url] : [])} title={album.title} />
        <span className="min-w-0 flex-1">
          <span className="font-display block text-lg sm:text-xl text-slate-50 leading-tight truncate">{album.title}</span>
          <span className="mt-0.5 block text-[10px] uppercase tracking-[0.2em] text-amber-400/90">
            {t("gallery.photosCount", { n: album.photos?.length || 0 })}
          </span>
        </span>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-colors ${open ? "border-amber-400/60 text-amber-400" : "border-slate-700 text-slate-400"}`}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-800/70 px-3.5 sm:px-4 py-3" data-testid={`album-photos-${album.id}`}>
              {album.description && <p className="mb-3 text-xs sm:text-sm text-slate-400 leading-relaxed">{album.description}</p>}
              {album.photos?.length ? (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                  {album.photos.map((p, i) => (
                    <motion.button
                      key={p + i}
                      initial={{ opacity: 0, scale: 0.92 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.35, delay: Math.min(i * 0.04, 0.4) }}
                      onClick={() => onPhoto(album.id, i)}
                      className="group/ph relative block aspect-square overflow-hidden rounded-lg border border-slate-800/70 focus:outline-none"
                    >
                      <img src={imgSrc(p, 400)} alt={`${album.title} ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover/ph:scale-[1.08]" />
                      <span className="absolute inset-0 bg-[#090D14]/0 transition-colors duration-300 group-hover/ph:bg-[#090D14]/25" />
                    </motion.button>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-500 italic">{t("gallery.emptyPhotos")}</p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Gallery() {
  const { t } = useLang();
  const [albums, setAlbums] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [lightbox, setLightbox] = useState(null); // { albumId, index }
  const lbPushed = useRef(false);
  const touchX = useRef(null);
  const mobile = useIsMobile();
  const [peek, setPeek] = useState(false);
  const visibleAlbums = !mobile || peek ? albums || [] : (albums || []).slice(0, 2);

  useEffect(() => {
    api.get("/albums").then((r) => setAlbums(r.data)).catch(() => setAlbums([]));
  }, []);

  const lbAlbum = lightbox ? (albums || []).find((a) => a.id === lightbox.albumId) : null;
  const lbPhotos = lbAlbum?.photos || [];
  const lbCount = lbPhotos.length;

  const openLightbox = (albumId, index) => {
    setLightbox({ albumId, index });
    window.history.pushState({ gbLightbox: true }, "");
    lbPushed.current = true;
  };
  const closeLightbox = () => {
    setLightbox(null);
    if (lbPushed.current) {
      lbPushed.current = false;
      window.history.back();
    }
  };
  const navPhoto = (dir) => {
    if (!lbCount) return;
    setLightbox((lb) => (lb ? { ...lb, index: (lb.index + dir + lbCount) % lbCount } : lb));
  };

  // tasto indietro del telefono/browser: chiude la foto e resta sull'album aperto
  useEffect(() => {
    const onPop = () => {
      if (lightbox) {
        lbPushed.current = false;
        setLightbox(null);
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [lightbox]);

  // frecce da tastiera + Esc
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") navPhoto(1);
      if (e.key === "ArrowLeft") navPhoto(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, lbCount]);

  return (
    <section id="gallery" className="py-14 sm:py-28 bg-[#0B101B]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400">{t("gallery.eyebrow")}</span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-slate-50">
            {t("gallery.titleA")}<span className="text-amber-400">{t("gallery.titleAccent")}</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400">{t("gallery.subtitle")}</p>
        </Reveal>

        <div className="mt-10 space-y-2.5" data-testid="album-list">
          {albums === null && <p className="text-sm text-slate-500">{t("gallery.loading")}</p>}
          {visibleAlbums.map((a, i) => (
              <Reveal key={a.id} delay={0.05 * i} y={24}>
                <AlbumRow
                  album={a}
                  open={openId === a.id}
                  onToggle={() => setOpenId(openId === a.id ? null : a.id)}
                  onPhoto={openLightbox}
                />
              </Reveal>
            ))}
        </div>
        {mobile && (albums || []).length > 2 && (
          <ExpandPill open={peek} onToggle={() => setPeek(!peek)} hiddenCount={(albums || []).length - 2} testid="expand-gallery" />
        )}
      </div>

      <AnimatePresence>
        {lightbox && lbAlbum && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeLightbox}
            onTouchStart={(e) => { const x = e.touches?.[0]?.clientX; touchX.current = x ?? null; }}
            onTouchEnd={(e) => {
              const x = e.changedTouches?.[0]?.clientX;
              if (touchX.current === null || x === undefined) return;
              const dx = x - touchX.current;
              touchX.current = null;
              if (Math.abs(dx) > 50) navPhoto(dx < 0 ? 1 : -1);
            }}
            className="fixed inset-0 z-[80] flex items-center justify-center bg-[#090D14]/95 backdrop-blur-md p-4"
          >
            <button
              onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
              data-testid="lightbox-close"
              className="absolute top-5 right-5 z-10 text-slate-300 hover:text-amber-400"
              aria-label={t("gallery.close")}
            >
              <X className="h-8 w-8" />
            </button>
            {lbCount > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); navPhoto(-1); }}
                  data-testid="lightbox-prev"
                  aria-label={t("gallery.prev")}
                  className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-[#090D14]/70 text-slate-200 transition-colors hover:border-amber-400 hover:text-amber-400"
                >
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); navPhoto(1); }}
                  data-testid="lightbox-next"
                  aria-label={t("gallery.next")}
                  className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 flex h-11 w-11 items-center justify-center rounded-full border border-slate-700 bg-[#090D14]/70 text-slate-200 transition-colors hover:border-amber-400 hover:text-amber-400"
                >
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
            <motion.img
              key={`${lightbox.albumId}-${lightbox.index}`}
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              src={imgSrc(lbPhotos[lightbox.index])}
              alt={`${lbAlbum.title} ${lightbox.index + 1}`}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full rounded-xl border border-slate-700 object-contain"
            />
            {lbCount > 1 && (
              <span data-testid="lightbox-counter" className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full border border-slate-700 bg-[#090D14]/70 px-4 py-1.5 text-[11px] font-bold tracking-[0.2em] text-slate-300">
                {lightbox.index + 1} / {lbCount}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
